/**
 * Handles dragging tasks from the grid onto the schedule
 */
import {DragHelper, MessageDialog} from "@bryntum/schedulerpro";
import { taskRenderer } from "../../../other/utils/bgUtils.jsx";
import {getWorkingRangesInScheduler} from "../../../other/utils/schedulerUtils.jsx";
import { getSetting } from "../../../react/context/bryntumContext.jsx";
import { ViewType } from "../../../other/constants/OtherKeys.jsx";
import { MIXED_TASK_COLOR, MIXED_TASK_STYLE } from "../../../other/constants/TaskTypes.jsx";

export default class TaskDrag extends DragHelper {
  static get configurable() {
    return {
      // Don't drag the actual row element, clone it
      cloneTarget: true,
      mode: "translateXY",
      // Only allow drops on the schedule area
      dropTargetSelector: ".b-timeline-subgrid .b-grid-row",
      // Only allow drag regular rows
      targetSelector: ".b-grid-row:not(.b-group-row) .b-sch-event",

      listeners: {
        dragstart: "onTaskDragStart",
        drop: "onTaskDrop",
        beforeDragStart: "onTaskBeforeDragStart",
      }
    };
  }

  // Since user can from TaskFinder that is opened from app header finder icon... and if user attempts to open the finder
  // from Schedule page and then lets say user moved to a different page called Client page and then dragging that task from the Finder will crash the app
  onTaskBeforeDragStart() {
    const { schedule } = this;

    return !schedule?.isDestroyed;
  }

  onTaskDragStart({ context, event }) {
    const { schedule } = this,
      proxy = context.element,
      task = this.grid.getRecordFromElement(context.grabbed);

    // save a reference to the Task being dragged so we can access it later
    context.task = task;
    // Mutate dragged element (grid row) into an event bar
    proxy.classList.remove("b-grid-row");

    const globalTaskColor = getSetting("taskColor", MIXED_TASK_COLOR, true);
    const globalTaskStyle = getSetting("taskStyle", MIXED_TASK_STYLE, true);
    const viewType = getSetting("viewType", ViewType.normal);
    proxy.innerHTML = taskRenderer(task, globalTaskColor, globalTaskStyle, viewType);
    // By default, this element has `pointer-events: all`, which causes issues when dropping items on the timeline.
    // Instead of getting the actual target (e.g., the time axis sub-grid), we receive the event proxy element.
    // By unsetting the pointer event we'll get the time axis sub-grid as reference for the dropping element
    proxy.querySelector(".b-sch-event-wrap").style["pointer-events"] = "unset";

    // schedule.enableScrollingCloseToEdges(schedule.timeAxisSubGrid);

    // Bug: If there is an editor opened, proxy element will be visible behind that editor
    proxy.style.minHeight = proxy.style.height;
    proxy.style["flex-direction"] = "row"; // using row so the proxy element can take the full width
    proxy.style.height = "auto";
    proxy.style.zIndex = 999999;
    proxy.style.left = (event.clientX - (new DOMMatrix(proxy.style.transform).m41)) + "px";

    // Prevent tooltips from showing while dragging
    schedule.element.classList.add("b-dragging-event");

    context.validityOverlapIcon = proxy.querySelector(".validity-overlap i");
  }

  // Drop callback after a mouse up, take action and create event (bit of work) related to the dropped task
  async onTaskDrop({ context }) {
    const { schedule } = this,
      { target } = context;

    schedule.disableScrollingCloseToEdges(schedule.timeAxisSubGrid);

    // If drop was done in a valid location, set the startDate and transfer the task to the Scheduler event store
    const targetResource = schedule.getRecordFromElement(target);
    if (context.valid && targetResource && !(targetResource.isGroupHeader || targetResource.isSpecialRow)) {
      const state = schedule.project.crudStores.map(s => { const state = s.store.reapplyFilterOnUpdate; s.store.reapplyFilterOnUpdate = false; return state; })
      const promise = schedule.project.commitAsync();
      let startDate = schedule.getDateFromCoordinate(context.clientX, "floor", false);
      const eventsToAdd = [], assignmentsToAdd = [];
      getWorkingRangesInScheduler(startDate, schedule).forEach((dateRange) => {
        const newEvent = schedule.eventStore.createRecord({
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          taskId: context.task.id
        });
        const newAssignment = schedule.assignmentStore.createRecord({
          resourceId: targetResource.id,
          eventId: newEvent.id
        })
        eventsToAdd.push(newEvent);
        assignmentsToAdd.push(newAssignment);
      })
      if (eventsToAdd.length >= 50) {
        const response = await MessageDialog.confirm({
          okButton: "Proceed",
          cancelButton: "Cancel",
          title: `Creating ${eventsToAdd.length} task events`,
          message: `${eventsToAdd.length} are being created at the same time which could take some to resolve`
        })
        if (response.okButton) {
          schedule.eventStore.add(eventsToAdd);
          schedule.assignmentStore.add(assignmentsToAdd);
        }
      } else {
        schedule.eventStore.add(eventsToAdd);
        schedule.assignmentStore.add(assignmentsToAdd);
      }
      await promise;
      schedule.project.crudStores.map((s, index) => {
        s.store.reapplyFilterOnUpdate = state[index]
        s.store.filter();
      })
      // Finalize the drag operation
      context.finalize();
    }
    // Dropped somewhere undesired, abort
    else {
      this.abort();
    }
    schedule.element.classList.remove("b-dragging-event");
  }
}
